<template lang="pug">
section.ml-integration
  h4.ml-integration__title Configuración de Mercado Libre
  p.ml-integration__description
    | Algunos reportes de ventas de Mercado Libre traen un código alfanumérico en "Número de seguimiento"
    | en vez del número de envío de la etiqueta. Al conectar tu cuenta, 12 Horas Envíos usa el "# de venta"
    | para obtener el número de envío real y así el escaneo del QR funciona normalmente.
    | Solo se pide permiso de lectura de ventas y envíos.

  p.ml-integration__status(v-if="loading") Cargando…

  template(v-else-if="integration && integration.connected")
    p.ml-integration__status
      | Cuenta conectada:
      strong.ml-integration__nickname {{ integration.nickname || integration.userId }}
    .ml-integration__actions
      AppButton(variant="primary" outline :disabled="busy" @click="connect") Volver a conectar
      AppButton(variant="danger" outline :disabled="busy" @click="disconnect") Desconectar

  template(v-else)
    .ml-integration__actions
      AppButton(variant="primary" :disabled="busy || !storeId" @click="connect") Conectar con Mercado Libre
    p.ml-integration__hint Serás redirigido a Mercado Libre para autorizar el acceso de tu cuenta.

  ul.ml-integration__unresolved(v-if="integration && hasUnresolved")
    li.ml-integration__unresolved-item(v-if="integration.unresolved.pending")
      | {{ integration.unresolved.pending }} envío(s) en proceso de obtener su número de envío.
    li.ml-integration__unresolved-item.ml-integration__unresolved-item--warning(v-if="integration.unresolved.notConnected")
      | {{ integration.unresolved.notConnected }} envío(s) esperan que conectes Mercado Libre para obtener su número de envío.
    li.ml-integration__unresolved-item.ml-integration__unresolved-item--danger(v-if="integration.unresolved.failed")
      | {{ integration.unresolved.failed }} envío(s) no se pudieron resolver; ingresa su número de envío manualmente.
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AppButton from '../UI/AppButton.vue'

export default {
  name: 'MercadolibreIntegrationConfig',
  components: { AppButton },
  data() {
    return {
      loading: false,
      busy: false,
      integration: null
    }
  },
  computed: {
    ...mapGetters(['user']),
    storeId() {
      return this.user.role === 'store' ? this.user.storeId : null
    },
    hasUnresolved() {
      const unresolved = this.integration.unresolved || {}
      return Boolean(unresolved.pending || unresolved.notConnected || unresolved.failed)
    }
  },
  mounted() {
    this.fetchIntegration()
  },
  methods: {
    ...mapActions(['getMercadolibreAuthorizeUrl', 'getMercadolibreIntegration', 'disconnectMercadolibreIntegration']),
    showError(message) {
      this.$bvToast.toast(message, { title: 'Error', variant: 'danger' })
    },
    errorMessage(error, fallback) {
      return (error.response && error.response.data && error.response.data.error) || fallback
    },
    async fetchIntegration() {
      if (!this.storeId) return
      this.loading = true
      try {
        const response = await this.getMercadolibreIntegration(this.storeId)
        this.integration = response.data.integration
      } catch (error) {
        this.showError('No se pudo obtener el estado de la integración con Mercado Libre')
      } finally {
        this.loading = false
      }
    },
    async connect() {
      if (!this.storeId) {
        this.showError('No se pudo determinar la tienda')
        return
      }
      this.busy = true
      try {
        const response = await this.getMercadolibreAuthorizeUrl(this.storeId)
        window.location.href = response.data.url
      } catch (error) {
        this.busy = false
        this.showError(this.errorMessage(error, 'No se pudo iniciar la conexión con Mercado Libre'))
      }
    },
    async disconnect() {
      this.busy = true
      try {
        const response = await this.disconnectMercadolibreIntegration(this.storeId)
        this.integration = response.data.integration
      } catch (error) {
        this.showError(this.errorMessage(error, 'No se pudo desconectar Mercado Libre'))
      } finally {
        this.busy = false
      }
    }
  }
}
</script>

<style scoped>
.ml-integration {
  margin-top: 1.5rem;
}

.ml-integration__title {
  margin-bottom: 0.5rem;
}

.ml-integration__description,
.ml-integration__status {
  margin-bottom: 0.75rem;
}

.ml-integration__nickname {
  margin-left: 0.25rem;
}

.ml-integration__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.ml-integration__hint {
  margin-top: 0.5rem;
  color: #6c757d;
}

.ml-integration__unresolved {
  margin: 1rem 0 0;
  padding-left: 1.25rem;
}

.ml-integration__unresolved-item--warning {
  color: #b7791f;
}

.ml-integration__unresolved-item--danger {
  color: #c53030;
}
</style>
